<template>
  <div class="info">
    <div class="info__image-wrapper" v-if="urls.image">
      <img class="image img-fluid" alt="image" :src="urls.image" />
    </div>
    <div class="info__meta">
      <h3 class="address-name">{{ campground.name }}</h3>
      <section class="section">
        <div class="row">
          <div class="col-12 col-md-4">
            <div class="info__label">{{ $t('generic.labels.address') }}</div>
          </div>
          <div class="col-12 col-md-8">
            <ul class="address-list">
              <li v-if="campground.street1">{{ campground.street1 }}</li>
              <li v-if="campground.street2">{{ campground.street2 }}</li>
              <li v-if="campground.city">{{ campground.zip_code }} {{ campground.city }}</li>
              <li v-if="campground.country">{{ getCountryByCode(campground.country) }}</li>
            </ul>
          </div>
        </div>
      </section>
      <section class="section" v-if="dates.length > 0">
        <div class="row">
          <div class="col-12 col-md-4">
            <div class="info__label">{{ $t('generic.labels.period') }}</div>
          </div>
          <div class="col-12 col-md-8">
            <div class="value" :class="{ 'value--no-padding' : isCompleted }">{{ getFormattedDate(dates[0]) }} -</div>
            <div class="value" :class="{ 'value--no-padding' : isCompleted }">{{ getFormattedDate(dates[1]) }}</div>
            <div class="nights" v-if="dates.length === 2">({{ numNights }} {{ $tc('generic.date.nights', numNights) }})</div>
          </div>
        </div>
        <div class="info__edit" v-if="!isCompleted" @click="goToStep(1, { number: 1, key: 'displaySelection', value: 'dates' })">
          <iconEditGrey class="icon"></iconEditGrey>
        </div>
      </section>
      <section class="section" v-if="guests && guests.length > 0">
        <div class="row">
          <div class="col-12 col-md-4">
            <div class="info__label">{{ $t('generic.labels.guests') }}</div>
          </div>
          <div class="col-12 col-md-8">
            <div class="wrapper" v-for="(guest, index) of getGuestData" :key="index">
              <span class="value" :class="{ 'value--no-padding' : isCompleted }">{{ guest.amount }}x {{ guest.name }}</span>
            </div>
          </div>
        </div>
        <div class="info__edit" v-if="!isCompleted" @click="goToStep(1, { number: 1, key: 'displaySelection', value: 'persons' })">
          <iconEditGrey class="icon"></iconEditGrey>
        </div>
      </section>
    </div>
    <div class="info__booking" v-if="!isCompleted">
      <CiButton
        class="button--tertiary-outline info__booking__button"
        :class="{ 'button--primary-outline': isCompleted }" @click="toggleBookingDetails">
        <span>{{ $t('generic.buttons.showDetails') }}</span>
        <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
      </CiButton>
    </div>
    <div class="info__sum">
      <slot name="sum"></slot>
    </div>
  </div>
</template>

<script>
import { CiButton } from '@/components'
import bookingDataMixin from '@/mixins/bookingDataMixin'

import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconClose from '@/assets/icons/close-dark.svg'
import iconEditGrey from '@/assets/icons/edit-grey.svg'
import iconInfoBlack from '@/assets/icons/info-black.svg'
import iconInfoWhite from '@/assets/icons/info-white.svg'

export default {
  name: 'CiCampgroundInfo',
  mixins: [bookingDataMixin],
  components: {
    CiButton,
    iconArrowBlack,
    iconClose,
    iconEditGrey,
    iconInfoBlack,
    iconInfoWhite,
  },
  props: {
    campground: {
      type: Object,
      required: true,
    },
    confirmation: {
      type: Object,
      required: false,
    },
    countries: {
      type: Array,
      required: false,
    },
    currency: {
      type: Object,
      required: true,
    },
    dates: {
      type: Array,
      required: false,
    },
    guests: {
      type: Array,
      required: false,
    },
    isCompleted: {
      type: Boolean,
      default: false,
    },
    urls: {
      type: Object,
      required: true,
    },
    setStepStatus: {
      type: Function,
      required: false,
    },
    toggleBookingDetails: {
      type: Function,
      required: false,
    },
  },
  computed: {
    getGuestData() {
      return this.isCompleted
        ? this.guests
        : this.guests.map(guest => {
            const p = this.campground.persons.find(p => p.id === guest.id)
            return { ...p, ...guest }
          })
    },
  },
  methods: {
    goToStep(step, stepStatus) {
      this.setStepStatus({
        number: stepStatus.number,
        key: stepStatus.key,
        value: stepStatus.value,
      })
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: step },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.info {
  display: none;

  @include media-breakpoint-up(lg) {
    display: block;
    min-height: 100%;
    background: white;
    overflow: hidden;
    padding: 0 20px;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 50px;
  }
}

.info__image-wrapper {
  border-radius: 50%;
  border: 5px solid white;
  box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.01), 0 20px 48px 0 rgba(53, 53, 53, 0.17);
  margin: 50px 70px 40px;
  overflow: hidden;

  @include media-breakpoint-up(xl) {
    margin: 90px 70px 40px;
  }

  img {
    width: 100%;
  }
}

.info__meta {
  margin-top: 60px;

  .address-name {
    margin-bottom: 30px;
  }

  .info__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.25rem);
  }

  .address-list {
    @include font-format(0.875rem, 1.25rem);

    list-style: none;
    margin: 0;
    padding: 0;
  }

  .section {
    position: relative;
    margin: 0 0 25px;

    &:last-child {
      margin: 0;
    }

    .info__edit {
      position: absolute;
      top: 0;
      right: 0;
      cursor: pointer;
    }
  }

  .nights {
    color: #9f9f9f;
  }

  .value {
    display: block;
    padding-right: 45px;

    &.value--no-padding {
      padding: 0;
    }
  }
}

.info__image-wrapper + .info__meta {
  margin: 0;
}

.info__booking {
  margin: 40px 0;

  .icon {
    transform: translate3d(0, -50%, 0) rotate(180deg);
  }

  .no-touch &:hover span + .icon {
    transform: rotate(180deg) translate3d(-5px, 50%, 0);
  }

  .info__booking__button {
    margin: 0 auto;
    width: 100%;
  }
}

.info__sum {
  margin: 40px 0 40px -50px;

  .sum {
    padding-left: 35px;
  }
}
</style>
